import { useCallback } from "react";

import type { Story } from "@baiki/interfaces";

import { AnalyticsQueries } from "@/queries/analytics";
import type { PlayerState } from "@/store/store";

import type { PlayOptions } from "./usePlayer.types";

interface UsePlayerActionsProps {
  player: PlayerState;
  setPlayer: (
    update: PlayerState | ((prev: PlayerState) => PlayerState),
  ) => void;
}

export const usePlayerActions = ({
  player,
  setPlayer,
}: UsePlayerActionsProps) => {
  const trackPlayback = AnalyticsQueries.useTrackPlayback();

  /**
   * Play a story from the beginning
   * Resets currentTime to 0 and selects the appropriate track
   * @param options.seekToWord - Optional word index to seek to after loading
   */
  const play = useCallback(
    (story: Story, options?: PlayOptions) => {
      // Track playback analytics
      trackPlayback.mutate({
        storyId: story.id,
        source: "library",
      });

      // Determine which track to use
      const currentTrackBelongsToStory = story.content.tracks.some(
        (t) => t.id === player.track?.id,
      );

      const track = currentTrackBelongsToStory
        ? player.track
        : story.content.tracks[story.content.tracks.length - 1];

      // Calculate initial time if seeking to a word
      let initialTime = 0;
      if (options?.seekToWord !== undefined && track?.timings) {
        const wordIndex = options.seekToWord;
        if (wordIndex >= 0 && wordIndex < track.timings.words.length) {
          const word = track.timings.words[wordIndex];
          initialTime = Math.max(0, word.startTime - 0.15);
        }
      }

      setPlayer((prev) => ({
        ...prev,
        playing: true,
        buffering: false,
        currentTime: initialTime,
        duration: 0,
        story,
        track,
      }));
    },
    [player.track, setPlayer, trackPlayback],
  );

  /**
   * Resume playback without resetting state
   * Preserves currentTime and track
   */
  const resume = useCallback(
    (story: Story) => {
      setPlayer((prev) => ({
        ...prev,
        playing: true,
        story,
      }));
    },
    [setPlayer],
  );

  /**
   * Pause playback without changing story/track
   */
  const pause = useCallback(() => {
    setPlayer((prev) => ({
      ...prev,
      playing: false,
    }));
  }, [setPlayer]);

  /**
   * Stop playback and reset state
   */
  const stop = useCallback(() => {
    setPlayer((prev) => ({
      ...prev,
      playing: false,
      buffering: false,
      currentTime: 0,
      duration: 0,
      story: null,
      track: null,
    }));
  }, [setPlayer]);

  /**
   * Select a specific track for the current story
   */
  const selectTrack = useCallback(
    (trackId: string) => {
      if (!player.story) {
        return;
      }

      const track = player.story.content.tracks.find((t) => t.id === trackId);

      if (!track) {
        return;
      }

      setPlayer((prev) => ({
        ...prev,
        track,
        currentTime: 0,
        duration: 0,
      }));
    },
    [player.story, setPlayer],
  );

  return {
    play,
    resume,
    pause,
    stop,
    selectTrack,
  };
};
